import type { TrialRequest } from '~/gql/trial/trial'
import { useMutation, useQuery } from '@apollo/client/react'
import { Check, Inbox, Loader } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import {
  GQL_APPROVE_TRIAL,
  GQL_PENDING_TRIAL_REQUESTS,
  GQL_REJECT_TRIAL,
} from '~/gql/trial/trial'
import { client } from '~/helpers/apollo'
import { tourAnchor } from '~/helpers/tour/anchors'

/**
 * Revue plateforme des demandes d'essai (#155, SuperAdmin). Chaque ligne est une
 * demande dont l'e-mail est confirmé et qui attend une décision : approuver crée
 * l'espace et envoie l'activation, refuser prévient le demandeur (motif optionnel).
 */
function TrialRequests() {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_PENDING_TRIAL_REQUESTS, {
    client,
    fetchPolicy: 'network-only',
  })
  const [approve] = useMutation(GQL_APPROVE_TRIAL, { client })
  const [reject] = useMutation(GQL_REJECT_TRIAL, { client })

  const [busyId, setBusyId] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<TrialRequest | null>(null)
  const [reason, setReason] = useState('')

  const requests = (data?.pendingTrialRequests ?? []) as unknown as TrialRequest[]

  const handleApprove = async (req: TrialRequest) => {
    setBusyId(req.id)
    try {
      await approve({ variables: { id: req.id } })
      toast.success(t('trialRequests.approved', { org: req.orgName }))
      await refetch()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('trialRequests.error'))
    } finally {
      setBusyId(null)
    }
  }

  const handleReject = async () => {
    if (!rejecting) return
    setBusyId(rejecting.id)
    try {
      await reject({ variables: { id: rejecting.id, reason: reason.trim() || undefined } })
      toast.success(t('trialRequests.rejected', { org: rejecting.orgName }))
      setRejecting(null)
      setReason('')
      await refetch()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('trialRequests.error'))
    } finally {
      setBusyId(null)
    }
  }

  const formatDate = (value: string) => new Date(value).toLocaleDateString()

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <p className="text-sm text-muted-foreground" {...tourAnchor('trialRequests.intro')}>{t('trialRequests.intro')}</p>

      {requests.length === 0
        ? (
            // Liste vide ou tableau : la même ancre, l'un ou l'autre est à l'écran.
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center text-muted-foreground" {...tourAnchor('trialRequests.list')}>
              <Inbox className="size-8" />
              <p className="text-sm">{t('trialRequests.empty')}</p>
            </div>
          )
        : (
            <div className="overflow-x-auto rounded-lg border" {...tourAnchor('trialRequests.list')}>
              <Table aria-label={t('common.routing.trial-requests')}>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('trialRequests.col.org')}</TableHead>
                    <TableHead>{t('trialRequests.col.requester')}</TableHead>
                    <TableHead>{t('trialRequests.col.jobTitle')}</TableHead>
                    <TableHead>{t('trialRequests.col.confirmedAt')}</TableHead>
                    <TableHead className="text-right">{t('trialRequests.col.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map(req => (
                    <TableRow key={req.id}>
                      <TableCell className="font-medium">{req.orgName}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>
                            {req.firstName}
                            {' '}
                            {req.lastName}
                          </span>
                          <span className="text-xs text-muted-foreground">{req.email}</span>
                          {req.phone && <span className="text-xs text-muted-foreground">{req.phone}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{req.jobTitle || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(req.emailConfirmedAt)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2" {...tourAnchor('trialRequests.actions')}>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === req.id}
                            onClick={() => { setRejecting(req); setReason('') }}
                          >
                            {t('trialRequests.reject')}
                          </Button>
                          <Button
                            size="sm"
                            disabled={busyId === req.id}
                            onClick={() => handleApprove(req)}
                          >
                            {busyId === req.id
                              ? <Loader className="mr-2 size-4 animate-spin" />
                              : <Check className="mr-2 size-4" />}
                            {t('trialRequests.approve')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

      <Dialog open={rejecting !== null} onOpenChange={open => !open && setRejecting(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('trialRequests.rejectTitle', { org: rejecting?.orgName ?? '' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">{t('trialRequests.reasonLabel')}</Label>
            <textarea
              id="reject-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder={t('trialRequests.reasonPlaceholder')}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            />
            <p className="text-xs text-muted-foreground">{t('trialRequests.reasonHelp')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleReject} disabled={busyId === rejecting?.id}>
              {busyId === rejecting?.id && <Loader className="mr-2 size-4 animate-spin" />}
              {t('trialRequests.confirmReject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default TrialRequests
